<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">

import ConveyorTLBR from '@/components/Sections/Resources/Elements/Conveyor/ConveyorTLBR.vue'

import { defineProps, computed } from 'vue'
import type { PropType } from 'vue';
import type OreConversion from '@/types/OreConversion.ts'
import ConveyorTRBL from '@/components/Sections/Resources/Elements/Conveyor/ConveyorTRBL.vue'
import { usePlanetStore } from '@/stores/planetStore.ts'
import { CDN } from '@/models/cdn.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import StyledImage from '@/components/Elements/Images/StyledImage.vue'
import { useAssetStore } from '@/stores/assetStore.ts'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const planetStore = usePlanetStore()
const gameStore = useGameStore()
const assetStore = useAssetStore()

const openRingworldConversionModal = () => {
  if (hasRingworldTech.value) {
    gameStore.modalType = 'ringworldConversion'
  } else {
    const tech = assetStore.findTechByCode('ringworlds')
    if (tech) {
      gameStore.modalData = { techId: tech.id }
      gameStore.modalType = 'tech'
    }
  }
}

defineProps({
  left: {
    type: Number,
    required: true
  },
  top: {
    type: Number,
    required: true
  },
  height: {
    type: Number,
    required: true
  },
  width: {
    type: Number,
    required: true
  },
  ore: {
    type: Number,
    required: true
  },
  processed: {
    type: Object as PropType<OreConversion>,
    required: true
  },
  conveyorType: {
    type: String,
    required: false,
    default: 'left'
  },
  debug: {
    type: Boolean,
    required: false,
    default: false
  }
})

const ringworldCount = computed(() => {
  return planetStore.countPlanetsWithRingworldConversion()
})

const hasRingworldTech = computed(() => {
  const tech = assetStore.findTechByCode('ringworlds')
  return tech ? gameStore.hasPlayerTech(tech.id) : false
})

</script>

<template>
<div :class="{
          'absolute':true,
          'border':debug
        }"
     :style="'top:' + top + 'rem;left:' + left + 'rem;width:' + width + 'rem;height:' + height + 'rem;'">

  <!-- Left -->
  <ConveyorTLBR
    v-if="conveyorType == 'right'"
    :left="1.25"
    :top="1"
    :height="18"
    :width="3"
    :midTime="0.15"
    :min-speed="5"
    :object-image="CDN.icon('ore' + ore)"
    :conversion="processed"
    :debug="debug"
  />

  <!-- Right -->
  <ConveyorTRBL
    v-else-if="conveyorType == 'left'"
    :left="3.5"
    :top="1"
    :height="18"
    :width="4"
    :midTime="0.15"
    :min-speed="5"
    :object-image="CDN.icon('ore' + ore)"
    :conversion="processed"
    :debug="debug"
  />

  <div :class="{
            'absolute rounded bg-metal-800 border border-metal-700 hover:border-metal-600 cursor-pointer':true,
            'opacity-50':debug
          }"
       style="top:18rem;left:-0.8rem;width:9.6rem;z-index:15"
       @click="openRingworldConversionModal"
  >
    <div class="flex-col p-1">
      <div class="relative">
        <StyledImage
          :src="CDN.technology(128,'ringworlds')"
          alt="Ringworld Technology"
          size="full"
          :grayscale="!hasRingworldTech"
        />
        <div v-if="!hasRingworldTech" class="absolute inset-0 flex items-center justify-center bg-black/60 rounded-md">
          <span class="text-red-500 text-base text-center leading-tight" style="text-shadow: 0 0 12px rgba(0,0,0,1), 0 0 24px rgba(0,0,0,1), 0 0 36px rgba(0,0,0,0.9), 0 2px 6px rgba(0,0,0,1)">{{ t('resources.ringworld.techRequired') }}</span>
        </div>

        <!-- Status bar at bottom -->
        <div v-if="hasRingworldTech" class="absolute bottom-0 left-0 right-0 bg-black/60 py-1 text-center rounded-b-md">
          <span :class="ringworldCount > 0 ? 'text-green-400' : 'text-metal-400'" class="text-xs">
            {{ ringworldCount > 0 ? t('resources.ringworld.planetsBuilding', { count: ringworldCount }) : t('resources.ringworld.noConstruction') }}
          </span>
        </div>
      </div>
    </div>
  </div>

</div>
</template>

<style scoped lang="scss">

</style> 